import React, { useEffect, useRef, useState } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { MapPin } from "lucide-react";
import { sr } from "../../lib/sr";

const MI_TO_M = 1609.34;

// Small map inside Advanced setup so owners see the geo radius they're actually reaching.
// Uses plain Leaflet (not react-leaflet) with a single init/destroy effect so it is
// resilient to React StrictMode double-mounts and panel open/close toggles.
export default function ReachMap({ radiusMiles = 0 }) {
  const [centers, setCenters] = useState(null);
  const containerRef = useRef(null);
  const r = Number(radiusMiles) || 0;

  useEffect(() => {
    let alive = true;
    sr.locations()
      .then((res) => {
        const list = Array.isArray(res) ? res : res?.locations || [];
        const pts = list
          .map((l) => ({ lat: Number(l.lat), lng: Number(l.lng), name: l.name || l.address || "Your business" }))
          .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng) && (p.lat !== 0 || p.lng !== 0));
        if (alive) setCenters(pts);
      })
      .catch(() => { if (alive) setCenters([]); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!centers || centers.length === 0 || !containerRef.current) return undefined;
    const map = L.map(containerRef.current, { scrollWheelZoom: false, attributionControl: true });
    map.setView([centers[0].lat, centers[0].lng], 12);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      subdomains: "abc", maxZoom: 19, attribution: "&copy; OpenStreetMap contributors",
    }).addTo(map);
    const group = L.featureGroup().addTo(map);
    centers.forEach((c) => {
      L.circleMarker([c.lat, c.lng], { radius: 5, color: "#0B1220", fillColor: "#B7791F", fillOpacity: 1, weight: 2 }).addTo(group);
      if (r > 0) {
        L.circle([c.lat, c.lng], { radius: r * MI_TO_M, color: "#2E6BE6", fillColor: "#2E6BE6", fillOpacity: 0.12, weight: 1.5 }).addTo(group);
      }
    });
    const t = setTimeout(() => {
      map.invalidateSize();
      try {
        const b = group.getBounds();
        if (b.isValid()) map.fitBounds(b.pad(0.4), { maxZoom: 15 });
      } catch { /* keep default view */ }
    }, 80);
    return () => { clearTimeout(t); map.remove(); };
  }, [centers, r]);

  if (centers === null || centers.length === 0) return null; // loading, or no geocoded location — keep the panel clean

  return (
    <div data-testid="reach-map" style={{ marginBottom: 14 }}>
      <div className="sr-muted" data-testid="reach-map-caption" style={{ fontSize: 12, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
        <MapPin size={13} color="var(--blue)" />
        {r > 0 ? (
          <span>Your reach area — <strong style={{ color: "var(--navy)" }}>{r} mi</strong> around {centers.length > 1 ? `${centers.length} locations` : "your business"}</span>
        ) : (
          <span>Your business location{centers.length > 1 ? "s" : ""} — add a radius in Geo targeting to preview the reach ring</span>
        )}
      </div>
      <div ref={containerRef} data-testid="reach-map-canvas" style={{ height: 200, borderRadius: 12, overflow: "hidden", border: "1px solid var(--border)" }} />
    </div>
  );
}
